import type { VehicleInsurance } from '@makemydeal/dr-platform-types';

// Mirrors InsuranceProvidersFormatted from dr-dash-types, redeclared here so shared-components
// stays free of any dash-types dependency.
export type InsuranceProviderOption = {
    label: string;
    additionalText?: string;
    Name: string;
    Address?: string;
    City?: string;
    State?: string;
    ZIP?: string;
    Phone?: string;
    Abbreviation?: string;
};

/**
 * Imperative handle exposed via forwardRef.
 * The form is asked about its state — the parent decides what to do.
 */
export type InsuranceFormRef = {
    validate: () => Promise<boolean>;
    getValues: () => VehicleInsurance;
    isDirty: () => boolean;
};

export type ContractApiErroredVehicleInsuranceInfo = {
    hasApiInsuranceCompanyError: boolean;
    hasApiAgentPhoneNumberError: boolean;
    hasApiPolicyNumberError: boolean;
    hasApiPolicyStartDateError: boolean;
    hasApiPolicyExpirationDateError: boolean;
};

export const INSURANCE_FORM_ANALYTICS = {
    SEARCH_INSURANCE_PROVIDER_NAME: 'SEARCH_INSURANCE_PROVIDER_NAME',
    SELECT_INSURANCE_PROVIDER_NAME: 'SELECT_INSURANCE_PROVIDER_NAME',
    AUTOCOMPLETE_INSURANCE_PROVIDER_NAME: 'AUTOCOMPLETE_INSURANCE_PROVIDER_NAME'
} as const;

/**
 * Props contract for the presentation-only InsuranceForm.
 *
 * Orchestration (save, loading, footer, discard modal, analytics, dismiss)
 * is handled by the parent container — the form only renders fields and validates.
 */
export type InsuranceFormProps = {
    /** Current vehicle insurance payload. */
    vehicleInsuranceInfo?: VehicleInsurance | null;

    /** Feature toggle: enables additional insurance fields (agency name, bodily-injury). */
    isAdditionalInsuranceFieldsEnabled: boolean;

    /** Whether the insurance info has been updated externally. */
    isVehicleInsuranceUpdated?: boolean;

    /** Whether the deal is locked (disables all form inputs). */
    isLocked?: boolean;

    /** Buyer's state abbreviation for state-based contracting validation. */
    buyerState?: string | null;

    /** List of insurance provider options for the autocomplete field. */
    insuranceProviders: InsuranceProviderOption[];

    /** Enables contracting validation flow and contract-alert banner. */
    isContractingContext?: boolean;

    /** Fields the contract API reported as missing/invalid, so the form can show inline errors for them. */
    contractApiErroredVehicleInsurance?: ContractApiErroredVehicleInsuranceInfo | null;

    /** Notify parent when contracting-flow validator's error state flips. */
    onContractingErrorsAlert?: (hasErrors: boolean) => void;

    /** Fetches the insurance providers list. */
    getInsuranceProviders: () => void;

    /** Reports current dirty/valid state to parent on every change. */
    onChange?: (isDirty: boolean, isValid: boolean) => void;

    /** Propagates validation error flags to the container / store. */
    onSetErrorFields?: (entity: string, errorFlags: Record<string, boolean>) => void;

    /** Unified provider interaction analytics callback. */
    onProviderInteraction?: (type: (typeof INSURANCE_FORM_ANALYTICS)[keyof typeof INSURANCE_FORM_ANALYTICS]) => void;

    /** Host app identifier */
    hostApp?: 'MV' | 'SCI';

    /** Extra attributes merged into every New Relic page action. */
    logContext?: Record<string, string | number | boolean>;
};
